import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";

import { SmallButton, Button } from "@/components/Buttons";
import Table from "@/components/Table";
import {
  DELIVERY_STATUS_6,
  DELIVERY_STATUS_7,
  DELIVERY_STATUS_11,
} from "@/Models/Delivery";
import { DeliveryForRiderModal, WaybillModal } from "@/Modals";
import {
  CASE_ALL,
  CASE_CANCELED,
  CASE_LOST,
  CASE_NOT_PRINTED,
  CASE_SENT_BACK,
} from "@/store/HanjinReturnWaybills";
import {
  useDeliveriesStore,
  useHanjinReturnWaybillsStore,
  useLoadingStore,
  useModalStore,
} from "@/store/hooks";
import { SIZE_TYPE_150_HANJIN } from "@/utils/Print";

const Title = styled.h1`
  font-size: 36px;
  margin-bottom: 20px;
`;

const StyledRow = styled(Row)`
  margin-left: -5px;
  margin-right: -5px;
  margin-bottom: 5px;
`;

const StyledCol = styled(Col)`
  padding-left: 5px;
  padding-right: 5px;
`;

const SearchRow = styled.div`
  margin-bottom: 10px;
`;

const DatePickerCol = styled(StyledCol)`
  display: flex;
  flex-direction: row;
  align-items: center;

  input {
    width: calc(50% - 15px);
  }
`;

const Wave = styled.span`
  margin: 0 5px;
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)`
  margin-right: 10px;
  margin-bottom: 6px;
  min-width: 120px;
`;

const MetaRow = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ClickableColumn = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const PrintedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ printed }) => (printed ? "#eaf3ec" : "#fdecec")};
  color: ${({ printed }) => (printed ? "#2f7536" : "#c0392b")};
`;

const renderReturnReceiptType = (row) => {
  if (row.status === DELIVERY_STATUS_6) {
    return "반송완료";
  }

  if (row.status === DELIVERY_STATUS_7) {
    return "분실완료";
  }

  if (row.status === DELIVERY_STATUS_11 || row.isCanceled) {
    return "화주사요청취소";
  }

  return "반품접수";
};

const BookIdColumn = ({ delivery }) => {
  const { openModal } = useModalStore();

  function handleClick() {
    openModal(
      <DeliveryForRiderModal
        backdrop
        key="book-detail-modal"
        bookId={delivery.bookId}
        type={delivery.type || "3"}
        defaultSizeType={SIZE_TYPE_150_HANJIN}
        hanjinOnly
      />,
    );
  }

  return (
    <ClickableColumn onClick={handleClick}>
      {delivery.renderBookId ? delivery.renderBookId() : delivery.bookId}
    </ClickableColumn>
  );
};

const PrintColumn = memo(({ delivery, onPrint }) => {
  return (
    <SmallButton
      variant={delivery.printedOnAdmin ? "secondary" : "info"}
      onClick={() => onPrint([delivery])}
    >
      {delivery.printedOnAdmin ? "재출력" : "운송장출력"}
    </SmallButton>
  );
});

const HanjinReturnWaybills = () => {
  const { state, ...actions } = useHanjinReturnWaybillsStore();
  const { ...deliveriesActions } = useDeliveriesStore();
  const { finishLoading, startLoading } = useLoadingStore();
  const { openModal } = useModalStore();
  const { handleSubmit, register, reset, setValue } = useForm();
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);

  useEffect(() => {
    reset({
      dateFrom: state.query.dateFrom,
      dateTo: state.query.dateTo,
      bookId: state.query.bookId,
      orderIdFromCorp: state.query.orderIdFromCorp,
      orderIdFromCorpSub: state.query.orderIdFromCorpSub,
    });
  }, []);

  useEffect(() => {
    fetchAll();
  }, [
    state.query.page,
    state.query.pageSize,
    state.query.dateFrom,
    state.query.dateTo,
    state.query.bookId,
    state.query.orderIdFromCorp,
    state.query.orderIdFromCorpSub,
    state.query.caseFilter,
  ]);

  const fetchAll = async () => {
    try {
      startLoading();
      await actions.fetchAll();
    } catch (e) {
      window.alert(
        `반품송장 내역을 불러오는데 실패하였습니다.\n에러메시지: ${e.message}`,
      );
    } finally {
      finishLoading();
    }
  };

  const openWaybill = useCallback(
    async (deliveries) => {
      if (!deliveries?.length) {
        window.alert("출력할 접수건을 선택해주세요.");
        return;
      }

      try {
        startLoading();

        const waybillDeliveries =
          await deliveriesActions.fetchAllSelectedDeliveries(deliveries);

        const deliveriesWithInputAddress = await Promise.all(
          waybillDeliveries.map(async (delivery) => {
            if (delivery.senderAddressError) {
              const addressByBookId = await deliveriesActions.fetchAddressById(
                delivery.bookId,
              );

              return {
                ...delivery,
                senderInputAddress: addressByBookId.senderInputAddress,
                receiverInputAddress: addressByBookId.receiverInputAddress,
              };
            }

            return delivery;
          }),
        );

        openModal(
          <WaybillModal
            key="waybill-modal"
            deliveries={deliveriesWithInputAddress}
            defaultSizeType={SIZE_TYPE_150_HANJIN}
            hanjinOnly
            onAfterSubmit={fetchAll}
          />,
        );
      } catch (e) {
        window.alert(
          `운송장 출력을 위한 배송목록 조회에 실패하였습니다.\n에러메시지: ${e.message}`,
        );
      } finally {
        finishLoading();
      }
    },
    [
      deliveriesActions.fetchAllSelectedDeliveries,
      deliveriesActions.fetchAddressById,
      openModal,
    ],
  );

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSetPageSize = useCallback(
    (pageSize) => {
      actions.setQuery({ page: 1, pageSize });
    },
    [actions.setQuery],
  );

  const handleSetCaseFilter = useCallback(
    (caseFilter) => {
      actions.setQuery({
        caseFilter,
        page: 1,
      });
    },
    [actions.setQuery],
  );

  const onSubmit = useCallback(
    ({ dateFrom, dateTo, bookId, orderIdFromCorp, orderIdFromCorpSub }) => {
      actions.setQuery({
        dateFrom,
        dateTo,
        bookId,
        orderIdFromCorp,
        orderIdFromCorpSub,
        page: 1,
      });
    },
    [actions.setQuery],
  );

  const handleReset = useCallback(() => {
    const query = actions.resetQuery();
    reset({
      dateFrom: query.dateFrom,
      dateTo: query.dateTo,
      bookId: "",
      orderIdFromCorp: "",
      orderIdFromCorpSub: "",
    });
    setValue("dateFrom", query.dateFrom);
    setValue("dateTo", query.dateTo);
  }, [actions.resetQuery, reset, setValue]);

  const handleSelect = useCallback(({ selectedRows }) => {
    setSelectedDeliveries(selectedRows);
  }, []);

  const handleClickPrintSelected = useCallback(() => {
    openWaybill(selectedDeliveries);
  }, [openWaybill, selectedDeliveries]);

  const handleClickPrintNotPrinted = useCallback(async () => {
    try {
      startLoading();
      const deliveriesNotPrinted = await actions.fetchNotPrinted();

      if (!deliveriesNotPrinted?.length) {
        window.alert("미출력 접수건이 0건입니다.");
        return;
      }

      await openWaybill(deliveriesNotPrinted);
    } catch (e) {
      window.alert(`미출력 접수건 조회에 실패하였습니다.\n${e.message}`);
    } finally {
      finishLoading();
    }
  }, [actions.fetchNotPrinted, openWaybill]);

  const columns = useMemo(
    () => [
      {
        Header: "예약번호",
        accessor: (row) => <BookIdColumn delivery={row} />,
        selectable: false,
        width: 140,
      },
      {
        Header: "접수일시",
        accessor: (row) => row.renderReceiptDate(),
        width: 120,
      },
      {
        Header: "반품접수유형",
        accessor: (row) => renderReturnReceiptType(row),
        width: 130,
      },
      {
        Header: "사측주문번호",
        accessor: (row) => row.orderIdFromCorp || "",
        width: 140,
      },
      {
        Header: "주문자구분번호",
        accessor: (row) =>
          row.orderIdFromCorpSub || row.etc4Obj?.orderIdFromCorpSub || "",
        width: 130,
      },
      {
        Header: "기업회원",
        accessor: (row) => row.corpUserCorpTitle,
        width: 150,
      },
      {
        Header: "접수점명",
        accessor: (row) => row.spotName,
        width: 150,
      },
      {
        Header: "송하인명",
        accessor: (row) => row.senderName,
        width: 150,
      },
      {
        Header: "출발동",
        accessor: (row) => row.senderDongName,
        width: 90,
      },
      {
        Header: "운송장출력여부",
        accessor: (row) => (
          <PrintedBadge printed={!!row.printedOnAdmin}>
            {row.printedOnAdmin ? "출력완료" : "미출력"}
          </PrintedBadge>
        ),
        width: 120,
      },
      {
        Header: "처리",
        accessor: (row) => <PrintColumn delivery={row} onPrint={openWaybill} />,
        selectable: false,
        width: 110,
      },
      {
        Header: "수지",
        accessor: (row) => row.renderPickupDateScheduled("HH:mm"),
        width: 60,
      },
      {
        Header: "수완",
        accessor: (row) => row.renderPickupDateCompleted("HH:mm"),
        width: 60,
      },
      {
        Header: "입고",
        accessor: (row) => row.renderWarehousedAt("HH:mm"),
        width: 60,
      },
      {
        Header: "배차",
        accessor: (row) => row.renderDeliveryAllocatedDate(),
        width: 90,
      },
      {
        Header: "연기",
        accessor: (row) => row.renderPostponedDate(),
        width: 90,
      },
      {
        Header: "배출",
        accessor: (row) => row.renderReleasedAt(),
        width: 90,
      },
      {
        Header: "배완",
        accessor: (row) => row.renderDeliveryCompletedDate(),
        width: 90,
      },
      {
        Header: "지역",
        accessor: (row) =>
          `${row.receiverSidoName || ""} ${row.receiverSigunguName || ""}`.trim(),
        width: 150,
      },
      {
        Header: "도착동",
        accessor: (row) => row.receiverDongName,
        width: 90,
      },
      {
        Header: "도로명",
        accessor: (row) => row.receiverAddressRoadWithoutSigungu,
        width: 150,
      },
      {
        Header: "수거 담당",
        accessor: (row) => row.pickupRiderName,
        width: 80,
      },
      {
        Header: "배송 담당",
        accessor: (row) => row.deliveryRiderName,
        width: 80,
      },
      {
        Header: "메인운송장",
        accessor: (row) => (row.printedOnAdmin ? "O" : "X"),
        width: 80,
      },
      {
        Header: "서브운송장",
        accessor: (row) => (row.printedOnSubAdmin ? "O" : "X"),
        width: 80,
      },
    ],
    [openWaybill],
  );

  return (
    <Container fluid>
      <Title>한진 화주사 반품송장 출력</Title>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <SearchRow>
          <StyledRow>
            <StyledCol md={2}>
              <Form.Control as="select" custom disabled>
                <option>반품접수일</option>
              </Form.Control>
            </StyledCol>
            <DatePickerCol md={4}>
              <Form.Control name="dateFrom" type="date" ref={register} />
              <Wave>~</Wave>
              <Form.Control name="dateTo" type="date" ref={register} />
            </DatePickerCol>
            <StyledCol md={3}>
              <Form.Control
                name="bookId"
                placeholder="송장번호(bookId)"
                ref={register}
              />
            </StyledCol>
            <StyledCol md={3}>
              <Form.Control
                name="orderIdFromCorp"
                placeholder="사측주문번호"
                ref={register}
              />
            </StyledCol>
          </StyledRow>
          <StyledRow>
            <StyledCol md={3}>
              <Form.Control
                name="orderIdFromCorpSub"
                placeholder="주문자구분번호"
                ref={register}
              />
            </StyledCol>
            <StyledCol md={2}>
              <Button
                type="button"
                variant="light"
                style={{ width: "100%" }}
                onClick={handleReset}
              >
                초기화
              </Button>
            </StyledCol>
            <StyledCol md={2}>
              <Button type="submit" style={{ width: "100%" }}>
                조회
              </Button>
            </StyledCol>
          </StyledRow>
        </SearchRow>
      </Form>

      <ButtonWrap>
        <StyledButton
          onClick={() => handleSetCaseFilter(CASE_ALL)}
          variant={state.query.caseFilter === CASE_ALL ? "info" : "light"}
        >
          전체
        </StyledButton>
        <StyledButton
          onClick={() => handleSetCaseFilter(CASE_SENT_BACK)}
          variant={state.query.caseFilter === CASE_SENT_BACK ? "info" : "light"}
        >
          반송완료 &gt; 반품접수건
        </StyledButton>
        <StyledButton
          onClick={() => handleSetCaseFilter(CASE_LOST)}
          variant={state.query.caseFilter === CASE_LOST ? "info" : "light"}
        >
          분실완료 &gt; 반품접수건
        </StyledButton>
        <StyledButton
          onClick={() => handleSetCaseFilter(CASE_CANCELED)}
          variant={state.query.caseFilter === CASE_CANCELED ? "info" : "light"}
        >
          화주사요청취소 &gt; 반품접수건
        </StyledButton>
        <StyledButton
          onClick={() => handleSetCaseFilter(CASE_NOT_PRINTED)}
          variant={
            state.query.caseFilter === CASE_NOT_PRINTED ? "info" : "light"
          }
        >
          미출력건
        </StyledButton>
      </ButtonWrap>

      <MetaRow>
        <span>{selectedDeliveries.length}건 선택됨</span>
        <ButtonWrap style={{ marginBottom: 0 }}>
          <StyledButton variant="success" onClick={handleClickPrintSelected}>
            운송장출력(선택건)
          </StyledButton>
          <StyledButton variant="success" onClick={handleClickPrintNotPrinted}>
            운송장출력(미출력건)
          </StyledButton>
        </ButtonWrap>
      </MetaRow>

      <Table
        checkbox
        responsive
        bordered
        columns={columns}
        currentPage={state.query.page}
        data={state.deliveries}
        goToPage={handleGoToPage}
        nextPage={handleNextPage}
        pageCount={state.pageCount}
        pageSize={state.query.pageSize}
        previousPage={handlePreviousPage}
        setPageSize={handleSetPageSize}
        onSelect={handleSelect}
      />
    </Container>
  );
};

export default HanjinReturnWaybills;
